import React, { useState, useRef } from 'react';
import DiagramEditor from './DiagramEditor';
import RepomixImportModal from './RepomixImportModal';
import useRepomixToBeaUX, { Filter } from '../hooks/useRepomixToBeaUX';
import { convertLayoutToBeaUXState } from '../hooks/useMermaidToBeaUX';
import { MessageCircle, Send } from 'lucide-react';

// Sample filter prompts for user guidance
const SAMPLE_PROMPTS = [
  "show all components",
  "show files related to DiagramEditor",
  "visualize hooks directory",
  "find components using useMermaidToBeaUX",
  "show me the utils folder",
];

const RepomixExplorer: React.FC = () => {
  const [isRepomixModalOpen, setIsRepomixModalOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const diagramEditorRef = useRef<{ importDiagram: (nodes: any[], connections: any[]) => void } | null>(null);
  const { processRepomixXml, applyFilter, hasData } = useRepomixToBeaUX();

  // Handle Repomix XML import
  const handleRepomixImport = async (xmlContent: string): Promise<{ error: string | null }> => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Process the XML content
      const result = await processRepomixXml(xmlContent);
      
      if (result.error) {
        setError(result.error);
        return { error: result.error };
      }
      
      if (!result.layout || result.layout.nodes.length === 0) {
        const errorMsg = "No nodes found in the Repomix XML.";
        setError(errorMsg);
        return { error: errorMsg };
      }
      
      // Convert to beaUX format
      const { nodes: beaUXNodes, connections: beaUXConnections } = 
        convertLayoutToBeaUXState(result.layout);
      
      // Import the diagram via the ref
      if (diagramEditorRef.current) {
        diagramEditorRef.current.importDiagram(beaUXNodes, beaUXConnections);
      } else {
        const errorMsg = "Could not access diagram editor. Please try again.";
        setError(errorMsg);
        return { error: errorMsg };
      }
      
      return { error: null };
    } catch (err: any) {
      const errorMsg = `Error importing Repomix XML: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      return { error: errorMsg };
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle chat query submission
  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !hasData || isProcessing) return;
    
    try {
      setIsProcessing(true);
      setError(null);
      
      // For MVP, we'll directly translate the query to a filter object
      // In a production app, this would be handled by an AI service via backend
      const filter = translateQueryToFilter(chatInput);
      
      // Apply the filter to the data
      const result = await applyFilter(filter);
      
      if (result.error) {
        setError(result.error);
        return;
      }
      
      if (!result.layout || result.layout.nodes.length === 0) {
        setError("No matching nodes found for your query.");
        return;
      }
      
      // Convert to beaUX format
      const { nodes: beaUXNodes, connections: beaUXConnections } = 
        convertLayoutToBeaUXState(result.layout);
      
      // Import the diagram via the ref
      if (diagramEditorRef.current) {
        diagramEditorRef.current.importDiagram(beaUXNodes, beaUXConnections);
      }
      
      setChatInput('');
    } catch (err: any) {
      setError(`Error processing query: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Simple translation of natural language to filter (in MVP)
  // In production, this would be handled by an AI service
  const translateQueryToFilter = (query: string): Filter => {
    const lowerQuery = query.toLowerCase();
    
    // Extract potential file path if it exists in the query
    const filePathMatch = query.match(/["']([^"']+\.[a-z]+)["']/i);
    const filePath = filePathMatch ? filePathMatch[1] : null;
    
    // Check for component-related queries
    if (lowerQuery.includes('component')) {
      if (filePath || lowerQuery.includes('using') || lowerQuery.includes('related')) {
        return {
          filter_type: "related_to_file",
          value: filePath || "src/components", 
          node_types: ["component"],
          depth: 1
        };
      } else {
        return {
          filter_type: "node_type",
          value: "component"
        };
      }
    }
    
    // Check for hook-related queries
    if (lowerQuery.includes('hook')) {
      return {
        filter_type: "node_type",
        value: "hook"
      };
    }
    
    // Check for utility-related queries
    if (lowerQuery.includes('util')) {
      return {
        filter_type: "node_type",
        value: "util"
      };
    }
    
    // Check for page-related queries
    if (lowerQuery.includes('page')) {
      return {
        filter_type: "node_type",
        value: "page"
      };
    }
    
    // Check for specific file relation queries
    if ((lowerQuery.includes('related') || lowerQuery.includes('using')) && filePath) {
      return {
        filter_type: "related_to_file",
        value: filePath,
        depth: 1
      };
    }
    
    // Default: name contains search
    // Extract main keywords by removing common words
    const keywords = lowerQuery
      .replace(/show|me|the|all|for|find|visualize|display/g, '')
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 2)
      .join(' ');
    
    return {
      filter_type: "name_contains",
      value: keywords || "src" // Default to showing src if no keywords
    };
  };

  // Handle clicking on a node in the diagram
  const handleNodeSelection = (nodeId: string) => {
    setSelectedNode(nodeId);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top panel - controls */}
      <div className="p-4 border-b flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Codebase Explorer</h2>
          <p className="text-sm text-muted-foreground">
            {hasData 
              ? "Use natural language to explore your codebase structure" 
              : "Upload a Repomix XML file to visualize your codebase"}
          </p>
        </div>
        
        <button
          onClick={() => setIsRepomixModalOpen(true)}
          className="px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-md text-sm font-medium"
        >
          {hasData ? "Import New File" : "Upload Repomix XML"}
        </button>
      </div>
      
      {/* Main diagram area */}
      <div className="flex-1 relative">
        <DiagramEditor ref={diagramEditorRef} />
        
        {/* Loading overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <div className="bg-white p-4 rounded-md shadow-lg">
              <p className="text-md font-medium">Processing...</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom panel - chat input */}
      <div className="p-4 border-t">
        {/* Selected node info */}
        {selectedNode && (
          <div className="mb-3 px-3 py-2 bg-blue-50 text-blue-800 rounded text-sm">
            <span className="font-medium">Selected: </span>
            {selectedNode}
          </div>
        )}
        
        {/* Error display */}
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 text-red-600 rounded text-sm">
            {error}
          </div>
        )}
        
        {hasData ? (
          <>
            {/* Sample prompts */}
            <div className="mb-3 flex flex-wrap gap-2">
              {SAMPLE_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setChatInput(prompt)}
                  className="px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-xs"
                >
                  {prompt}
                </button>
              ))}
            </div>
            
            {/* Chat input */}
            <form onSubmit={handleChatSubmit} className="flex items-center gap-2">
              <div className="flex-1 relative">
                <MessageCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask a question about your codebase..."
                  className="w-full pl-9 pr-4 py-2 border rounded-md"
                  disabled={isProcessing}
                />
              </div>
              <button
                type="submit"
                disabled={!chatInput.trim() || isProcessing}
                className="p-2 bg-primary text-white rounded-md disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            Upload a Repomix XML file to start exploring your codebase
          </div>
        )}
      </div>
      
      {/* Import Modal */}
      <RepomixImportModal
        isOpen={isRepomixModalOpen}
        onClose={() => setIsRepomixModalOpen(false)}
        onImport={handleRepomixImport}
      />
    </div>
  );
};

export default RepomixExplorer;